import OrdersService from '@/api/services/orders.service';
import TablesService from '@/api/services/tables.service';
import SectorsService from '@/api/services/sectors.service';
import TableSessionsService from '@/api/services/table-sessions.service';
import type { IGetClosedSessionsFilters } from '@/api/services/table-sessions.service';
import { HostToolError, type IGpthelpTool } from '../tool-types';
import {
  getLimitArg,
  getOptionalNumberArray,
  getOptionalString,
  getPageArg,
  getRequiredString,
  normalizeInstantRange,
  RANGE_END_DESCRIPTION,
} from '../tool-args';
import { runToolApiCall } from '../tool-http';
import { sanitizeOrderRow, sanitizeTableRow } from '../sanitize';
import { ensureOrdersHistoryAccess } from './orders-tools';

export const tablesTools: IGpthelpTool[] = [
  {
    definition: {
      name: 'tables_list',
      description:
        'The tables configured for the selected branch (name, capacity, sector, enabled). Use it to resolve table names/ids before filtering sessions or orders. For live occupancy use orders_find with pending statuses.',
      sideEffects: 'none',
      parameters: { type: 'object', additionalProperties: false, properties: {} },
    },
    policy: 'read',
    isAvailable: (gate) => gate.hasRestaurantScope,
    async execute(_args, { scope }) {
      const [tables, sectors] = await runToolApiCall(() =>
        Promise.all([
          TablesService.getAll(scope.restaurantId, scope.branchId),
          SectorsService.getAll(scope.restaurantId, scope.branchId),
        ]),
      );

      const sectorsById = new Map(
        (sectors ?? []).map((s) => [s.id, { id: s.id, name: s.name }]),
      );

      const results = (tables ?? []).map((t) => sanitizeTableRow(t, sectorsById));
      return { count: results.length, results };
    },
  },
  {
    definition: {
      name: 'tables_findClosedSessions',
      description:
        "Closed table sittings (the history page's Mesas tab): one row per closed session grouping all its orders, with total spent and invoice state. Sittings whose orders were ALL canceled/declined are excluded. For the orders of one session follow up with tables_getSessionOrders.",
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          search: {
            type: 'string',
            description:
              'Table name (including deleted tables) or customer name.',
          },
          tableIds: {
            type: 'array',
            items: { type: 'number' },
            maxItems: 20,
            description: 'Explicit table filter (resolve ids via tables_list).',
          },
          dateFrom: {
            type: 'string',
            description:
              'Start date (yyyy-MM-dd, branch-local) or ISO instant; applied on closedAt. 90-day fallback when absent.',
          },
          dateTo: { type: 'string', description: RANGE_END_DESCRIPTION },
          page: { type: 'number', minimum: 1, default: 1 },
          limit: { type: 'number', minimum: 1, maximum: 50, default: 20 },
        },
      },
    },
    policy: 'read',
    readsDinerText: true,
    isAvailable: (gate) => gate.hasRestaurantScope && gate.canViewOrdersHistory,
    async execute(args, { deps, scope }) {
      ensureOrdersHistoryAccess(deps);

      const search = getOptionalString(args, 'search');
      const tableIds = getOptionalNumberArray(args, 'tableIds', 20);
      const page = getPageArg(args);
      const limit = getLimitArg(args, { def: 20, max: 50 });

      const { from, to } = normalizeInstantRange({
        obj: args,
        fromKey: 'dateFrom',
        toKey: 'dateTo',
        timeZone: scope.timeZone,
      });

      const filters: IGetClosedSessionsFilters = {
        page,
        itemsPerPage: limit,
      };
      if (search) filters.search = search;
      if (tableIds?.length) {
        filters.tableIds = tableIds.map((n) => Math.floor(n));
      }
      if (from) filters.dateFrom = from.toISOString();
      if (to) filters.dateTo = to.toISOString();

      const data = await runToolApiCall(() =>
        TableSessionsService.getClosedSessions(
          scope.restaurantId,
          scope.branchId,
          filters,
        ),
      );

      // Rows are already compact and PII-free: id, tableId, tableName,
      // closedAt, ordersCount, total, invoiceState.
      return { total: data.total, page, results: data.items ?? [] };
    },
  },
  {
    definition: {
      name: 'tables_getSessionOrders',
      description:
        'The orders of one closed table session (sessionId from tables_findClosedSessions). For full detail on one order follow up with orders_get.',
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          sessionId: {
            type: 'string',
            description: 'Table session UUID.',
          },
        },
        required: ['sessionId'],
      },
    },
    policy: 'read',
    readsDinerText: true,
    isAvailable: (gate) => gate.hasRestaurantScope && gate.canViewOrdersHistory,
    async execute(args, { deps, scope }) {
      ensureOrdersHistoryAccess(deps);

      const sessionId = getRequiredString(args, 'sessionId');

      const orders = await runToolApiCall(() =>
        OrdersService.getByTableSession(sessionId),
      );

      // Client tenant fence: every order must belong to the selected restaurant.
      if ((orders ?? []).some((o) => o.restaurantId !== scope.restaurantId)) {
        throw new HostToolError('NOT_FOUND', 'Sesión de mesa no encontrada', {
          sessionId,
        });
      }

      const results = (orders ?? []).map(sanitizeOrderRow);
      return { count: results.length, results };
    },
  },
];
